/**
 * ChangeSetPanel.tsx — review and publication, in one place.
 *
 * The buttons here are offered according to §8.3's state machine, but the
 * server is what enforces it — including separation of duties, which means the
 * person who authored a change set cannot be the one who approves it. That
 * refusal is deliberately NOT pre-empted in the UI: a reviewer who is told
 * "you cannot approve your own work" by the server has learned the rule, where
 * a greyed-out button teaches nothing.
 */

import { useState } from "react";
import { Empty, Panel } from "../../components/Panel";
import { StatusPill } from "../../components/StatusPill";
import { canDo, type ChangeSetController, type ConflictReport } from "./useChangeSet";

export function ChangeSetPanel({ controller }: { controller: ChangeSetController }): JSX.Element {
  const [note, setNote] = useState("");
  const [message, setMessage] = useState("");
  const changeSet = controller.changeSet;
  const status = changeSet?.status;

  return (
    <Panel
      title="Change set"
      actions={changeSet ? <StatusPill status={changeSet.status} /> : undefined}
    >
      {!changeSet ? <Empty>No change set yet. Your first edit will open one.</Empty> : (
        <>
          <dl className="facts">
            <dt>Title</dt><dd>{changeSet.title}</dd>
            <dt>Author</dt><dd>{changeSet.createdBy}</dd>
            <dt>Base revision</dt><dd><code>{changeSet.baseCanonicalRevision.slice(0, 12)}</code></dd>
            <dt>Operations</dt><dd>{changeSet.operationCount}</dd>
            <dt>Files touched</dt><dd>{changeSet.changedResources.length}</dd>
            {changeSet.resultCanonicalRevision && (
              <>
                <dt>Published as</dt>
                <dd><code>{changeSet.resultCanonicalRevision.slice(0, 12)}</code></dd>
              </>
            )}
          </dl>

          {changeSet.reviewState.approvals.length > 0 && (
            <p className="muted">Approved by {changeSet.reviewState.approvals.map(a => a.subject).join(", ")}</p>
          )}
          {changeSet.reviewState.changesRequested.length > 0 && (
            <ul className="findings">
              {changeSet.reviewState.changesRequested.map((entry, index) => (
                <li key={index} className="severity-warning">
                  <strong>{entry.subject}</strong> <span>{entry.note}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="row">
            <button type="button" onClick={() => void controller.submit()} disabled={controller.busy || !canDo(status, "submit")}>
              Submit for review
            </button>
            <button type="button" onClick={() => void controller.approve()} disabled={controller.busy || !canDo(status, "approve")}>
              Approve
            </button>
            <button type="button" className="ghost" onClick={() => void controller.abandon()} disabled={controller.busy || !canDo(status, "abandon")}>
              Abandon
            </button>
          </div>

          {canDo(status, "requestChanges") && (
            <div className="row">
              <input
                type="text" value={note} placeholder="What needs changing"
                onChange={event => setNote(event.target.value)}
                aria-label="Requested change"
              />
              <button type="button" onClick={() => { void controller.requestChanges(note); setNote(""); }} disabled={controller.busy || !note.trim()}>
                Request changes
              </button>
            </div>
          )}

          {canDo(status, "publish") && (
            <div className="row">
              <input
                type="text" value={message} placeholder="Commit message (optional)"
                onChange={event => setMessage(event.target.value)}
                aria-label="Commit message"
              />
              <button type="button" onClick={() => void controller.publish(message || undefined)} disabled={controller.busy}>
                Publish
              </button>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}

/**
 * §9 — the conflict, stated in full.
 *
 * A workbench that silently reloaded here would be discarding the user's work
 * to resolve a disagreement it is not entitled to resolve. So both revisions
 * are named, who moved it is named, and reloading is the user's decision.
 */
export function ConflictNotice({ conflict, onReload, onDismiss }: {
  conflict: ConflictReport;
  onReload: () => void;
  onDismiss: () => void;
}): JSX.Element {
  return (
    <div className="banner banner-conflict" role="alert">
      <div>
        <strong>Someone else changed this</strong>
        <p>{conflict.message}</p>
        <dl className="facts compact">
          {conflict.resource && <><dt>Resource</dt><dd><code>{conflict.resource}</code></dd></>}
          {conflict.expectedRevision !== undefined && <><dt>You had</dt><dd><code>{String(conflict.expectedRevision)}</code></dd></>}
          {conflict.currentRevision !== undefined && <><dt>Now</dt><dd><code>{String(conflict.currentRevision)}</code></dd></>}
          {conflict.changedBy !== undefined && <><dt>Changed by</dt><dd>{String(conflict.changedBy)}</dd></>}
          {conflict.changedAt !== undefined && <><dt>At</dt><dd>{String(conflict.changedAt)}</dd></>}
        </dl>
        {conflict.requestId && <p className="request-id">Request {conflict.requestId}</p>}
      </div>
      <div className="row">
        <button type="button" onClick={onReload}>Reload the diagram</button>
        <button type="button" className="ghost" onClick={onDismiss}>Keep looking</button>
      </div>
    </div>
  );
}
